import { messagesService } from '@/services/messages/messages.service'

/**
 * Browser stand-in for `Packages.ru.esoft.fatclient.tools.Messages`.
 * Every window goes through `messagesService` (Swing `UIMessages`), never `prompt`/`confirm`/`alert`.
 */
export const Messages = {
  /**
   * Swing `showInput(message, defaultValue)`. Title is the option-pane default, not the first argument.
   */
  showInput(message: string, defaultValue?: unknown): Promise<string | null> {
    return messagesService.showInput(message, defaultValue)
  },

  showInputWithTitle(title: string, message: string, defaultValue?: unknown): Promise<string | null> {
    return messagesService.showInputWithTitle(title, message, defaultValue)
  },

  /**
   * Swing `showInputText`.
   * 2-arg: (title, message). 3-arg: (message, title, def).
   */
  showInputText(
    titleOrMessage: string,
    messageOrTitle: string,
    def?: string,
  ): Promise<string> {
    if (def !== undefined) {
      return messagesService.showInputText(titleOrMessage, messageOrTitle, def)
    }
    return messagesService.showInputText(titleOrMessage, messageOrTitle)
  },

  showMessage(text: string, title?: string): Promise<void> {
    return messagesService.showMessage(String(text ?? ''), String(title ?? ''))
  },

  /**
   * Swing error option pane. Promise so scripts can await it.
   */
  showMessageError(text: string, title?: string): Promise<void> {
    return messagesService.showMessageError(String(text ?? ''), String(title ?? ''))
  },

  showToast(message: string | Record<string, unknown>): void {
    messagesService.showToast(message)
  },

  /**
   * Non-modal hint next to the calling button (or `anchorName`). Not a dialog.
   * `showTooltip(message)`, `showTooltip(message, title)`, `showTooltip(message, title, anchorName)`.
   */
  showTooltip(message: string, title?: string, anchorName?: string): void {
    messagesService.showTooltip(
      String(message ?? ''),
      title == null ? '' : String(title),
      anchorName == null ? '' : String(anchorName),
    )
  },

  showConfirmDialog(message: string, title: string): Promise<number> {
    return messagesService.showConfirmDialog(message, title)
  },

  checkConfirmation(message: string, title: string): Promise<boolean> {
    return messagesService.checkConfirmation(message, title)
  },

  showCheckBoxChooser(message: string, title: string, variants: any): Promise<any> {
    return messagesService.showCheckBoxChooser(message, title, variants)
  },

  showChooser(
    message: string,
    title: string,
    variants: any,
    keyColumn?: string,
    descriptionColumn?: string,
  ): Promise<string | null> {
    if (keyColumn != null && descriptionColumn != null) {
      return messagesService.showChooser(message, title, variants, keyColumn, descriptionColumn)
    }
    return messagesService.showChooser(message, title, variants)
  },

  showButtonChooser(
    message: string,
    title: string,
    variants: any,
    keyColumn?: string,
    descriptionColumn?: string,
  ): Promise<string | null> {
    if (keyColumn != null && descriptionColumn != null) {
      return messagesService.showButtonChooser(message, title, variants, keyColumn, descriptionColumn)
    }
    return messagesService.showButtonChooser(message, title, variants)
  },

  showButtonChooserByColumns(
    message: string,
    title: string,
    variants: any[][],
    buttonsOnRow: number,
  ): Promise<string | null> {
    return messagesService.showButtonChooserByColumns(message, title, variants, buttonsOnRow)
  },

  showFilesToSignChooser(
    title: string,
    files: any[],
    selectedIndices: number[],
    maxSize: number,
  ) {
    return messagesService.showFilesToSignChooser(title, files, selectedIndices, maxSize)
  },

  showFilesChooser(
    title: string,
    files: any[],
    selectedIndices: number[],
    maxSize: number,
    overLimitMessage: string,
    canBeEmpty: boolean,
  ) {
    return messagesService.showFilesChooser(
      title,
      files,
      selectedIndices,
      maxSize,
      overLimitMessage,
      canBeEmpty,
    )
  },

  showChooserWithTable(
    message: string,
    title: string,
    variants: any,
    descriptionColumn: string[],
  ): Promise<string | null> {
    return messagesService.showChooserWithTable(message, title, variants, descriptionColumn)
  },
}

export default Messages
